import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { LayoutDashboard, Users, UserPlus, PhoneCall, UserMinus, LogOut, Building2, History, Inbox, Repeat, Menu, X, ShieldAlert, Clock3 } from "lucide-react";

const NAV = [
  { to: "/", label: "Genel Bakış", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/ortaklar", label: "Ortaklar", icon: Users, testid: "nav-members" },
  { to: "/yeni-uye", label: "Yeni Üye Ekle", icon: UserPlus, testid: "nav-add" },
  { to: "/telefon", label: "Telefon Sorgulama", icon: PhoneCall, testid: "nav-phone" },
  { to: "/ayrilan", label: "Ayrılan Ortaklar", icon: UserMinus, testid: "nav-departed" },
  { to: "/devirler", label: "Hisse Devri Yapanlar", icon: Repeat, testid: "nav-devirs" },
  { to: "/gecmis", label: "Değişiklik Geçmişi", icon: History, testid: "nav-history" },
  { to: "/dilekceler", label: "Gelen Dilekçeler", icon: Inbox, testid: "nav-dilekce" },
  { to: "/ziyaretler", label: "Ortak Ziyaretleri", icon: Clock3, testid: "nav-visits" },
  { to: "/erisim", label: "Erişim Denemeleri", icon: ShieldAlert, testid: "nav-access" },
];

function SidebarInner({ user, onLogout, onNavClick }) {
  return (
    <>
      <div className="px-5 py-6 pr-12 flex items-center gap-3 border-b border-slate-200">
        <div className="h-9 w-9 rounded-md bg-slate-900 text-white flex items-center justify-center">
          <Building2 size={18} />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-bold tracking-tight text-slate-900">Ortaklık</div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 truncate">Yönetim Sistemi</div>
        </div>
      </div>
      <nav className="p-3 space-y-1 overflow-y-auto flex-1">
        {NAV.map(n => (
          <NavLink
            key={n.to} to={n.to} end={n.end}
            data-testid={n.testid}
            onClick={onNavClick}
            className={({ isActive }) => `side-link ${isActive ? "active" : ""}`}
          >
            <n.icon size={17} />{n.label}
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto p-3 border-t border-slate-200">
        <div className="flex items-center gap-3 p-2">
          {user.picture ? (
            <img src={user.picture} className="h-9 w-9 rounded-full object-cover" alt="" />
          ) : (
            <div className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center text-slate-700 text-sm font-semibold">
              {(user.name || user.email || "?").slice(0,1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">{user.name || user.email}</div>
            <div className="text-[11px] text-slate-500 truncate">{user.email}</div>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={onLogout}
          className="w-full mt-2 inline-flex items-center gap-2 justify-center text-sm rounded-md border border-slate-200 text-slate-700 hover:bg-slate-100 py-2"
        >
          <LogOut size={15} /> Çıkış Yap
        </button>
      </div>
    </>
  );
}

export default function AppShell({ user, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch (_) { /* ignore */ }
    navigate("/login", { replace: true });
  };

  // Close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Lock body scroll when drawer open
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col w-64 shrink-0 bg-white border-r border-slate-200">
        <SidebarInner user={user} onLogout={logout} />
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-200 h-14 flex items-center px-3 gap-3">
        <button
          type="button"
          data-testid="mobile-menu-btn"
          onClick={() => setMobileOpen(true)}
          aria-label="Menüyü aç"
          className="h-10 w-10 inline-flex items-center justify-center rounded-md border border-slate-200 text-slate-700 hover:bg-slate-100"
        >
          <Menu size={18} />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-8 w-8 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
            <Building2 size={16} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold tracking-tight text-slate-900 truncate">Ortaklık</div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 truncate">Yönetim Sistemi</div>
          </div>
        </div>
      </div>

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <div
          data-testid="mobile-drawer-backdrop"
          onClick={() => setMobileOpen(false)}
          className="md:hidden fixed inset-0 z-50 bg-slate-900/50"
        />
      )}

      {/* Mobile drawer */}
      <aside
        data-testid="mobile-drawer"
        className={`md:hidden fixed top-0 left-0 z-50 h-full w-72 max-w-[85vw] bg-white border-r border-slate-200 flex flex-col shadow-xl transition-transform duration-300 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        aria-hidden={!mobileOpen}
      >
        <button
          type="button"
          data-testid="mobile-drawer-close"
          onClick={() => setMobileOpen(false)}
          aria-label="Menüyü kapat"
          className="absolute top-3 right-3 h-9 w-9 inline-flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
        >
          <X size={18} />
        </button>
        <SidebarInner user={user} onLogout={logout} onNavClick={() => setMobileOpen(false)} />
      </aside>

      <main className="flex-1 min-w-0 pt-14 md:pt-0">
        <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">{children}</div>
      </main>
    </div>
  );
}
